import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Pct } from "@/components/layernav/primitives";
import { fmtBig, sectorTrack } from "./SectorHero";
import { ProtocolLogo } from "./ProtocolLogo";
import { timeAgo } from "@/lib/api";
import { entityNavRoute, entityTrack, resolveEntity, useEntityRegistry } from "@/lib/entities";

const GLASS = "rounded-xl border border-white/[0.09] bg-black/55 backdrop-blur-md";
const ROW = "grid grid-cols-[minmax(0,1fr)_76px_54px_64px] items-center gap-x-2";

const TokenCell = ({ token }: { token: any }) => {
  if (!token) {
    return <span className="font-mono text-[10px] text-gray-600 text-right block">—</span>;
  }
  const inner = (
    <span className="flex flex-col items-end leading-tight">
      <span className="font-mono text-[10px] font-bold text-[#A78BFA]">{token.symbol}</span>
      <Pct v={token.change_24h_pct} className="!text-[10px]" />
    </span>
  );
  const title = `${token.symbol} $${token.price_usd < 10 ? token.price_usd.toFixed(3) : token.price_usd.toLocaleString()} · ${token.source}`;
  if (token.has_coin_page) {
    return (
      <Link to={`/coin/${token.symbol}`} title={title}
        data-testid={`protocol-token-${token.symbol}`}
        onClick={(e) => { e.stopPropagation(); sectorTrack("market_module_interaction", `token_${token.symbol}`, "defi"); }}
        className="relative z-10 rounded hover:bg-white/[0.06] px-1 -mx-1 py-0.5 transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#A78BFA]/60">
        {inner}
      </Link>
    );
  }
  return <span title={title} data-testid={`protocol-token-${token.symbol}`} className="px-1 -mx-1 py-0.5">{inner}</span>;
};

export const ProtocolSnapshot = ({ data, testid = "protocol-snapshot", rows = 6 }: {
  data: any; testid?: string; rows?: number;
}) => {
  const protocols: any[] = (data?.protocols || []).slice(0, rows);
  const { data: reg } = useEntityRegistry();
  return (
    <div data-testid={testid} className={`${GLASS} p-4`}>
      <div className="flex items-center justify-between gap-2">
        <p className="font-mono text-[10px] font-bold uppercase tracking-[0.28em] text-[#34D399]">Protocol Snapshot</p>
        {data?.stale ? (
          <span data-testid="protocol-snapshot-stale" className="font-mono text-[8px] uppercase tracking-widest text-amber-400/80">cached</span>
        ) : data?.updated_at ? (
          <span className="font-mono text-[8px] uppercase tracking-widest text-gray-600">
            Upd {timeAgo(data.updated_at).replace(/ minutes?/, "m").replace(/ hours?/, "h").replace(" ago", "").replace("just now", "now")}
          </span>
        ) : null}
      </div>
      {protocols.length > 0 ? (
        <div className="mt-2.5">
          <div className={`${ROW} font-mono text-[9px] uppercase tracking-wider text-gray-500 mb-1`}>
            <span>Protocol</span><span className="text-right">TVL</span>
            <span className="text-right">24H</span><span className="text-right">Token 24H</span>
          </div>
          {protocols.map((p) => {
            const route = entityNavRoute(resolveEntity(reg, "protocol", p.slug));
            const meta = (
              <span className="block font-mono text-[9px] uppercase tracking-wide text-gray-400 truncate">
                {p.category}{p.dominant_chain ? ` · ${p.dominant_chain}` : ""}
                {p.chains?.length > 1 ? ` +${p.chains.length - 1}` : ""}
              </span>
            );
            return (
              <div key={p.slug} data-testid={`protocol-row-${p.slug}`} data-live={route ? "true" : "false"}
                className={`${ROW} group relative rounded-lg px-2 py-[7px] -mx-2 hover:bg-white/[0.04] transition-colors`}>
                <span className="flex items-center gap-2 min-w-0">
                  <span aria-hidden className="font-mono text-[9px] text-gray-500 w-3 shrink-0">{p.rank}</span>
                  <ProtocolLogo slug={p.slug} name={p.protocol} size={24}
                    className={route ? "transition-transform duration-200 group-hover:scale-[1.05]" : ""} />
                  {route ? (
                    <Link to={route} aria-label={`${p.protocol} — Entity Intelligence`}
                      data-testid={`entity-link-protocol-${p.slug}`}
                      onClick={() => entityTrack("entity_nav_click", `protocol:${p.slug}`, { surface: "protocol_snapshot" })}
                      className="min-w-0 focus-visible:outline-none after:absolute after:inset-0 after:rounded-lg focus-visible:after:ring-1 focus-visible:after:ring-[#00E5FF]/60">
                      <span className="flex items-center gap-1 min-w-0">
                        <span className="font-bold text-[13px] text-white leading-tight truncate transition-colors duration-200 group-hover:text-[#00E5FF]">{p.protocol}</span>
                        <ArrowUpRight aria-hidden
                          className="h-3 w-3 shrink-0 text-[#00E5FF] opacity-50 md:opacity-0 transition-opacity duration-200 md:group-hover:opacity-100 md:group-focus-within:opacity-100" />
                      </span>
                      {meta}
                    </Link>
                  ) : (
                    <span className="min-w-0">
                      <span className="block font-bold text-[13px] text-white leading-tight truncate">{p.protocol}</span>
                      {meta}
                    </span>
                  )}
                </span>
                <span className="font-mono text-[12.5px] font-semibold text-white text-right">{fmtBig(p.tvl_usd)}</span>
                <span className="text-right">
                  {p.tvl_change_24h_pct != null
                    ? <Pct v={p.tvl_change_24h_pct} className="!text-[11px]" />
                    : <span className="font-mono text-[10px] text-gray-600">—</span>}
                </span>
                <TokenCell token={p.token} />
              </div>
            );
          })}
        </div>
      ) : (
        <p data-testid="protocol-snapshot-unavailable" className="font-mono text-[11px] text-gray-600 py-4">
          Protocol data temporarily unavailable
        </p>
      )}
      <div className="mt-2 flex items-center justify-between gap-2">
        <p className="font-mono text-[8.5px] uppercase tracking-wider text-gray-500 leading-relaxed"
          title={data?.eligibility?.rule}>
          Live TVL rankings · DeFiLlama · Tokens via Kraken
        </p>
        <Link to="/defi/protocols" data-testid="protocol-snapshot-view-all"
          onClick={() => sectorTrack("market_module_interaction", "view_all_protocols", "defi")}
          className="group font-mono text-[10px] font-bold text-[#34D399] hover:text-white transition-colors whitespace-nowrap">
          View all →
        </Link>
      </div>
    </div>
  );
};
